import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Globe, Lock } from 'lucide-react'
import { useActor } from '@/hooks/useActor'
import { useAuth } from '@/hooks/useAuth'
import { isSuperAdminRoleName } from '@/lib/authz'
import { Button } from '@/components/ui/button'
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog'
import { RolesTab } from './RolesTab'
import { StaffTab } from './StaffTab'

export function SettingsPage() {
  const actor = useActor()
  const { identity } = useAuth()
  const [changePwOpen, setChangePwOpen] = useState(false)
  const isSuperAdmin = isSuperAdminRoleName(identity?.roleName)
  const isSupport = identity?.roleName === 'support'
  const canViewCourseAttempts = isSuperAdmin || isSupport
  const canViewWebsiteSync = isSuperAdmin

  return (
    <div className="rounded-xl border border-border/ bg-card/85 p-6 shadow-sm md:p-8">
      <div className="space-y-10">
        {/* Change Password Section — visible to everyone */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-foreground">Account Security</h2>
          <div className="flex items-center justify-between rounded-lg border border-border/ bg-muted/30 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Lock className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Change Password</p>
                <p className="text-xs text-muted-foreground">Update your account password at any time.</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChangePwOpen(true)}
              className="border-border/"
            >
              Change Password
            </Button>
          </div>
        </section>

        {/* Integrations */}
        {(canViewCourseAttempts || canViewWebsiteSync) && (
          <section>
            <h2 className="mb-4 text-lg font-semibold text-foreground">Integrations</h2>
            {canViewCourseAttempts ? (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-border/ bg-muted/30 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Calendar className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Course Attempts</p>
                    <p className="text-xs text-muted-foreground">
                      Registry of exam attempts from catalog sync and enrollments.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="border-border/" asChild>
                  <Link to="/settings/course-attempts">Open</Link>
                </Button>
              </div>
            ) : null}
            {canViewWebsiteSync ? (
              <div className="flex items-center justify-between rounded-lg border border-border/ bg-muted/30 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Globe className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Website Sync Console</p>
                    <p className="text-xs text-muted-foreground">
                      Pipeline controls, sync queue, worker runs, and go-live checklist.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="border-border/" asChild>
                  <Link to="/admin/website-sync">Open</Link>
                </Button>
              </div>
            ) : null}
          </section>
        )}

        {/* Roles & Staff — super admin only */}
        {isSuperAdmin && (
          <>
            <RolesTab actor={actor} />
            <StaffTab actor={actor} />
          </>
        )}
      </div>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  )
}
